import { Injectable } from '@angular/core';
import moment from 'moment';
import { UiAccountsControllerService } from '../cc-api/services';
import { VCSTokenExpirationDetails } from '../models/vcs-token-expiry-details';

import { TOKEN_EXPIRY_THRESHOLD } from '../components/token-expiry-user-note/token-expiry-constants';
import { catchError, map, of } from 'rxjs';
import cloneDeep from 'lodash/cloneDeep';

@Injectable({
  providedIn: 'root',
})
export class TokenExpiryService {
  expiringAccListLoaded: boolean = false;
  private expiringAccList: VCSTokenExpirationDetails[] = [];
  private inUseVCSAccounts: any[] = [];
  accountDetailsByAccId;
  accountDetailsByStackName;

  constructor(private accountControllerService: UiAccountsControllerService) {
    // this.extractExpiringAndExpiredVCSAccounts();
  }

  getExpiringAccList(): VCSTokenExpirationDetails[] {
    return this.expiringAccList;
  }

  getInUseVcsAccounts(): any[] {
    return this.inUseVCSAccounts;
  }

  async extractExpiringAndExpiredVCSAccounts() {
    this.expiringAccListLoaded = false;
    return new Promise((resolve, reject) => {
      Promise.all([
        this.getAllInUseVCSAccounts().toPromise(),
        this.getVCSExpiringAccountsList(),
      ]).then(async (data) => {
        this.inUseVCSAccounts = data[0];
        this.expiringAccList = data[1];
        this.inUseVCSAccounts = await this.addTokenExpiryDetails(
          this.inUseVCSAccounts,
        );
        const { accountIdMap, stackNameMap } = this.constructAccountsDetailsMap(
          this.inUseVCSAccounts,
        );
        this.accountDetailsByAccId = accountIdMap;
        this.accountDetailsByStackName = stackNameMap;
        this.expiringAccListLoaded = true;
        resolve(this.inUseVCSAccounts);
      });
    });
  }

  getAllInUseVCSAccounts = () => {
    return this.accountControllerService.getAllAccountsUsingGET().pipe(
      map((allAccounts) => {
        return allAccounts?.filter(
          (acc) => acc?.accountType === 'VERSION_CONTROL' && acc?.inUse,
        );
      }),
      catchError((error) => {
        return of([]);
      }),
    );
  };

  /*
   * Retrieves VCS accounts with expiration details from the API, and filters expired or expiring accounts
   * @returns {Promise<VCSTokenExpirationDetails[]>} A promise that resolves to the list of VCS accounts with expiration details, including both expired and expiring accounts.
   */
  getVCSExpiringAccountsList(): Promise<VCSTokenExpirationDetails[]> {
    return new Promise((resolve, reject) => {
      this.accountControllerService.getAllVCSTokenDetailsUsingGET().subscribe(
        async (response) => {
          // Get list of accounts whic are expired or expiring in next 7 days. Each account is of type: VCSTokenExpirationDetails
          this.expiringAccList = this.filterExpiringAccounts(response);
          resolve(this.expiringAccList);
        },
        (error) => {
          resolve([]);
        },
      );
    });
  }

  filterExpiringAccounts(allVCSAccounts): VCSTokenExpirationDetails[] {
    let expiringAccList = allVCSAccounts?.filter((acc) => {
      if (typeof acc?.expired !== 'boolean') {
        return false;
      }

      let daysUntilAccExpiry = this.daysUntilAccExpiry(acc?.expiryDate);
      if (
        acc?.expired ||
        (!acc?.expired &&
          acc?.expiryDate?.length &&
          daysUntilAccExpiry <= TOKEN_EXPIRY_THRESHOLD &&
          daysUntilAccExpiry >= 0)
      ) {
        acc['expiringInDays'] = !acc?.expired ? daysUntilAccExpiry : null;
        return true;
      }
      return false;
    });
    return expiringAccList;
  }

  daysUntilAccExpiry(expiryDate) {
    const expiry = moment.utc(expiryDate); // Parse expiryDate as UTC
    const currentDate = moment(); // Get current date in local timezone (IST)
    const expiryIST = expiry.local(); // Convert expiry date to IST
    const daysDifference = expiryIST.diff(currentDate, 'days');
    return daysDifference + 1;
  }

  addTokenExpiryDetails = async (accounts): Promise<any[]> => {
    const processedAccounts = await Promise.all(
      accounts?.map(async (account) => ({
        ...account,
        associatedTo: account?.associatedTo?.map((item) => item?.stackName),
        tokenExpiryDetails:
          this.expiringAccList?.find(
            (expiringAcc) => expiringAcc?.accountId === account?.id,
          ) || null,
      })),
    );

    return processedAccounts;
  };

  async getBlueprintAssociatedAccount(stackName) {
    if (!this.expiringAccListLoaded) {
      await this.extractExpiringAndExpiredVCSAccounts();
    }

    return this.accountDetailsByStackName[stackName];
  }

  async setSyncWithGitButtonState(
    actionButtons: any[],
    buttonId: string,
    stackName: string,
  ) {
    let clonedActionButtons = cloneDeep(actionButtons);
    const updatedActionButtons = await Promise.all(
      clonedActionButtons?.map(async (btn) => {
        if (btn?.id === buttonId) {
          const account = await this.getBlueprintAssociatedAccount(stackName);
          const isTokenExpired = account?.tokenExpiryDetails?.expired;
          return {
            ...btn,
            disabled: isTokenExpired,
            nbTooltip: isTokenExpired
              ? `Your linked VCS account ${account?.name}'s token has expired. Please renew to enable 'sync-with-git'.`
              : 'Not seeing a newly defined application in the blueprint? Click here.',
          };
        } else {
          return btn;
        }
      }),
    );

    return updatedActionButtons;
  }

  constructAccountsDetailsMap(allInUseVcsAccounts) {
    const accountIdMap = {};
    const stackNameMap = {};

    allInUseVcsAccounts.forEach((account) => {
      accountIdMap[account?.id] = { ...account };

      account?.associatedTo?.forEach((stackName) => {
        if (!stackNameMap[stackName]) {
          stackNameMap[stackName] = { ...account };
        }
      });
    });

    return { accountIdMap, stackNameMap };
  }
}
